import * as React from "react";
import groupBy from "lodash.groupby";
import Helmet from "react-helmet";
import Equip from "../../../components/Equip";
import Loading from "../../../components/Loading";
import { Wrap } from "../../../components/Layout/Page";
import { getPersisted, setPersisted } from "../../../utils/storage";
import { equipColumns } from "../../../utils/columns";
import { Props } from ".";
import { useInsightsEffects } from "../hooks/useInsightsEffects";
import { InsightType } from "../../../utils/routes";
import { navigate } from "../../../utils/routes/navigate";
import { useInsights } from "../../../context/insights";

const DEFAULT_SORT = {
  option: equipColumns.TRIGGERED_RULES.key,
  direction: equipColumns.TRIGGERED_RULES.sortDirection,
};

const PERSIST_KEY = "equipGrid";
const PERSIST_PROP = "Equip-colSort";

const InsightEquips: React.FC<Props> = (props) => {
  const initialSort = React.useMemo(
    () => getPersisted({ key: PERSIST_KEY, prop: PERSIST_PROP }, DEFAULT_SORT),
    []
  );
  const [sort, setSort] = React.useState(initialSort);
  const selectedSitesRef = React.useRef(JSON.stringify(props.selectedSites));
  const [state] = useInsights();

  useInsightsEffects(
    props.timeRange,
    [props.match.params.syncoId, ...props.selectedSites],
    props.shouldRequest,
    props.userSites.sites,
    props.userSites.projects
  );

  React.useEffect(() => {
    props.setMultiSite(props.match.params.syncoId);
  }, []);

  // * If selected sites changes, navigate back to insights
  React.useEffect(() => {
    if (selectedSitesRef.current !== JSON.stringify(props.selectedSites)) {
      props.setShouldRequest(true);
      navigate(props.urlHistory.insightsHistory.pathname)();
    }
  }, [props.selectedSites]);

  // Grid sort direction
  const handleSort = React.useCallback(
    (option: string) => () => {
      setSort((state) => {
        const direction = state.direction === "asc" ? "desc" : "asc";

        setPersisted(
          { key: PERSIST_KEY, prop: PERSIST_PROP },
          { option, direction }
        );
        return {
          option,
          direction,
        };
      });
    },
    []
  );

  // * NOTE: Checking against the data instead of state.loading
  // * as loading can mean more than just whether the data is available
  // * This way, updates to snoozed/associated don't flash the loading spinner
  // * Instead, the updated data will just appear (same as how monitoring works)
  // ! TODO: Create a 'no results' variation to account for invalid site ids
  if (!state.composedOccurrencesBySite[props.match.params.syncoId])
    return <Loading />;

  const siteGroup = state.composedOccurrencesBySite[props.match.params.syncoId];
  const ruleGroup = groupBy(siteGroup, (s) => s.ruleId)[
    props.match.params.ruleId
  ];
  const { ruleName } = ruleGroup[0];

  return (
    <>
      <Helmet title={`Insight - ${ruleName}`} />
      <Wrap>
        <Equip
          insightType={InsightType.Insights}
          hits={ruleGroup}
          ruleName={ruleName}
          syncoId={props.match.params.syncoId}
          activeSite={props.match.params.syncoId}
          ruleId={props.match.params.ruleId}
          showSnoozed={props.showSnoozed}
          sortOption={sort.option}
          sortDirection={sort.direction}
          updateSort={handleSort}
          showModal={props.showModal}
          urlHistory={props.urlHistory}
          selectedSites={props.selectedSites}
        />
      </Wrap>
    </>
  );
};

export default InsightEquips;
